import { SourcingStats } from 'api-clients/monolith';
import { clsx } from 'clsx';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { HiringOpeningsHoverCard } from '@/components/pages/dashboard/dashboard-table/hiring-openings-hover-card';
import { Typography, TypographyVariant } from '@/components/ui/typography';

import { Button } from './button';
import { Tooltip } from './tooltip';
import { Skeleton } from './skeleton';

export interface HireStatsProps {
  stats: SourcingStats;
  typographyVariant?: TypographyVariant;
  isHighlighted?: boolean;
  openingSlug: string;
  showOnlyActual?: boolean;
  hiringGoalUrl?: string;
}

export function HireStats({
  stats,
  typographyVariant = 'text',
  isHighlighted = false,
  openingSlug,
  showOnlyActual = false,
  hiringGoalUrl,
}: HireStatsProps) {
  const { expected, actual } = stats;

  const renderActual = () => {
    if (!actual) return '-';

    if (isHighlighted) {
      return (
        <HiringOpeningsHoverCard openingSlug={openingSlug}>
          <Typography
            variant={typographyVariant}
            className="!text-fx-blue bg-fx-blue-50 rounded-full px-1.5 py-0.5"
          >
            {actual}
          </Typography>
        </HiringOpeningsHoverCard>
      );
    }

    return <Typography variant={typographyVariant}>{actual}</Typography>;
  };

  if (showOnlyActual) {
    return <div className="flex items-center gap-1">{renderActual()}</div>;
  }

  if (hiringGoalUrl) {
    return (
      <div className="flex items-center gap-1">
        {renderActual()}
        <span>/</span>
        <Tooltip
          content={
            <FormattedMessage
              defaultMessage="Add a hiring goal to track sourcing performance, insights & recommendations to help you achieve your goal!"
              description="Tooltip on Add hiring goal link"
            />
          }
        >
          <Button
            variant="link"
            size="link"
            asChild
            className={clsx({
              '!text-large font-medium': typographyVariant === 'large2',
            })}
          >
            <Link to={hiringGoalUrl} target="_blank">
              <FormattedMessage defaultMessage="+ Add" description="Add link" />
            </Link>
          </Button>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {renderActual()}

      <>
        <span>/</span>
        <Typography variant={typographyVariant}>{expected ?? '-'}</Typography>
      </>
    </div>
  );
}

HireStats.Skeleton = function HireStatsSkeleton() {
  return (
    <div className="flex items-center gap-1">
      <Skeleton className="h-[20px] w-[100px]" />
      <span>/</span>
      <Skeleton className="h-[20px] w-[100px]" />
    </div>
  );
};
